import { ChevronDown, ChevronRight, PanelLeft, PanelRight } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as LogoAnimate } from '../../assets/images/LogoAnimate.svg';
import { MENU_SIDEBAR } from '../../config/menu.config';
import { Badge } from '../components/Badge';
import { cn } from '../lib/utils';

/**
 * Metronic Demo1 Sidebar Component
 * Professional CRM-style sidebar with accordion menu and collapse functionality
 *
 * @param {Object} props
 * @param {boolean} props.isCollapsed - Sidebar collapsed state
 * @param {Function} props.onToggle - Toggle sidebar handler
 */
export function Sidebar({ isCollapsed = false, onToggle }) {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (title) => {
    setExpandedItems(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (children) => {
    return children?.some(child => location.pathname === child.path);
  };

  const renderMenuItem = (item, index) => {
    // Heading/Section divider
    if (item.heading) {
      return (
        <div key={index} className="px-4 pt-5 pb-2">
          <span className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider">
            {item.heading}
          </span>
        </div>
      );
    }

    // Has children (expandable)
    if (item.children) {
      const isExpanded = expandedItems[item.title] || isParentActive(item.children);
      const hasActiveChild = isParentActive(item.children);

      return (
        <div key={index} className="px-3">
          <button
            onClick={() => toggleExpanded(item.title)}
            className={cn(
              'flex items-center justify-between w-full px-3 py-2.5 text-sm font-normal rounded-lg transition-colors',
              hasActiveChild
                ? 'text-primary bg-muted'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )}
          >
            <div className="flex items-center gap-3">
              {item.icon && <item.icon className="h-5 w-5" />}
              <span>{item.title}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* Sub-menu */}
          {isExpanded && (
            <div className="ml-8 mt-1 space-y-1">
              {item.children.map((child, childIndex) => (
                <Link
                  key={childIndex}
                  to={child.path}
                  className={cn(
                    'block px-3 py-2 text-sm font-normal rounded-lg transition-colors',
                    isActive(child.path)
                      ? 'text-primary bg-muted'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  {child.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Simple menu item
    return (
      <div key={index} className="px-3">
        <Link
          to={item.path}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 text-sm font-normal rounded-lg transition-colors',
            isActive(item.path)
              ? 'text-primary bg-muted'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          {item.icon && <item.icon className="h-5 w-5" />}
          <span className="flex-1">{item.title}</span>
          {item.badge && (
            <Badge variant={item.badgeVariant || 'default'} className="text-[10px] px-1.5 py-0">
              {item.badge}
            </Badge>
          )}
        </Link>
      </div>
    );
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => onToggle && onToggle(!isCollapsed)}
        className={cn(
          'fixed top-1/2 transform -translate-y-1/2 z-50',
          'bg-white border border-gray-200/80 rounded-r-lg px-2 py-3 shadow-lg',
          'hover:bg-gray-50 transition-all duration-300',
          isCollapsed ? 'left-0' : 'left-64'
        )}
      >
        {isCollapsed ? (
          <PanelRight className="w-5 h-5 text-gray-600" />
        ) : (
          <PanelLeft className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Sidebar */}
      <aside className={cn(
        'hidden lg:flex lg:flex-col lg:fixed lg:top-0 lg:bottom-0 lg:left-0 lg:w-64',
        'lg:border-r lg:border-gray-200/80 lg:bg-background lg:z-30',
        'transition-transform duration-300 ease-in-out',
        isCollapsed && '-translate-x-full'
      )}>
      {/* Logo Header */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200/80 shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8">
            <LogoAnimate className="w-full h-full" />
          </div>
          <span className="text-lg font-semibold text-foreground">ALICE</span>
        </Link>
      </div>

      {/* Navigation Menu */}
      <div className="flex-1 overflow-y-auto py-4 space-y-1">
        {MENU_SIDEBAR.map((item, index) => renderMenuItem(item, index))}
      </div>

      {/* Credits Footer Widget */}
      <div className="px-4 py-4 border-t border-gray-200/80 shrink-0">
        <div className="bg-primary/10 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Credits</p>
              <p className="text-2xl font-bold text-primary">150</p>
            </div>
          </div>
          <Link
            to="/billing"
            className="block text-center text-xs font-medium text-primary hover:text-primary/80 transition-colors mt-2"
          >
            Buy More →
          </Link>
        </div>
      </div>
    </aside>
    </>
  );
}

